import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { cn } from '../lib/utils';

export function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);
    setIsMobileMenuOpen(false);
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Bookings', path: '/book' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-primary font-sans text-secondary selection:bg-gold-base selection:text-primary">
      {/* Navigation */}
      <header
        className={cn(
          'fixed top-0 left-0 right-0 md:top-3 md:left-3 md:right-3 z-50 transition-all duration-300',
          isScrolled ? 'glass-panel py-4' : 'bg-transparent py-6'
        )}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              {location.pathname !== '/' && (
                <img src="/logo.png" alt="Skiva Africa Logo" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
              )}
              <span className="text-lg md:text-xl font-bold tracking-[0.2em] uppercase text-white group-hover:text-gold-light transition-colors">
                SKIVA <span className="text-gold-base">AFRICA TOURS</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8 text-xs font-semibold tracking-widest uppercase text-white/60">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'transition-colors',
                    location.pathname === link.path ? 'text-white border-b border-gold-base pb-1' : 'hover:text-white'
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="px-6 py-2 border border-gold-base text-gold-base text-[10px] font-bold uppercase tracking-widest hover:bg-gold-base hover:text-black transition-all duration-300 ml-4 rounded-sm"
              >
                Contact Us
              </Link>
            </nav>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-primary/95 backdrop-blur-xl pt-24 px-4 pb-6 flex flex-col md:hidden"
          >
            <nav className="flex flex-col gap-8 items-center justify-center flex-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'text-2xl font-extrabold uppercase tracking-wider',
                    location.pathname === link.path ? 'text-gold-base' : 'text-white'
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-4 px-8 py-4 border border-gold-base text-gold-base text-sm font-bold uppercase tracking-widest hover:bg-gold-base hover:text-black transition-all duration-300 rounded-sm"
              >
                Contact Us
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 w-full relative">
        {children}
      </main>

      {/* Footer */}
      <footer className="px-4 sm:px-6 md:px-10 py-8 bg-black border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-4 mt-auto">
        <div className="text-[10px] text-white/40 uppercase tracking-[0.2em] leading-relaxed">
          &copy; {new Date().getFullYear()} Skiva Africa Tours <br className="md:hidden" /> <span className="hidden md:inline">•</span> 5482 Sene St. Protea Glen, Ext 20, Johannesburg
        </div>
        <nav className="flex flex-wrap justify-center gap-6 text-[10px] font-bold uppercase tracking-widest text-white/50">
          <Link to="/services" className="hover:text-gold-base transition-colors">Explore Transport Services</Link>
          <Link to="/about" className="hover:text-gold-base transition-colors">About Us</Link>
          <Link to="/book" className="hover:text-gold-base transition-colors">Book a Private Transfer</Link>
          <Link to="/contact" className="hover:text-gold-base transition-colors">Contact Skiva Africa Tours</Link>
        </nav>
      </footer>
    </div>
  );
}
